import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators
} from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { PdfViewerModule } from 'ng2-pdf-viewer';
import { GestionService } from '../../core/services/gestion.service';
import { TiposDocumentoService } from '../../core/services/tipos-documento.service';
import { UiHelperService } from '../../services/ui-helper.service';
import { CustomSelectComponent } from '../shared/custom-select/custom-select.component';
import { calcularDigitoVerificacionNIT } from '../../core/utils/nit.util';
import {
  FACTURA_ANIO_MIN,
  FacturaFiltro,
  FacturaItem,
  armarNombreSaludo,
  nombreDesdeFactura,
  requiereAnioFactura,
  sanitizarNumeroFactura
} from '../../core/utils/factura.util';
import {
  TipoDocumentoOption,
  abreviaturaCanon,
  esAbreviaturaDocumentoNit,
  mensajeNumeroDocumentoInvalido,
  numeroDocumentoInvalido,
  sanitizarNumeroDocumento
} from '../../core/utils/tipos-documento.util';

const CONSULTA_REINTENTOS = 1;
const CONSULTA_REINTENTO_MS = 900;

@Component({
  selector: 'app-gestion-factura',
  templateUrl: './gestion-factura.component.html',
  styleUrls: ['./gestion-factura.component.css'],
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, CustomSelectComponent, PdfViewerModule]
})
export class GestionFacturaComponent implements OnInit, OnDestroy {
  readonly anioFacturaMin = FACTURA_ANIO_MIN;
  readonly filtros: { value: FacturaFiltro; label: string }[] = [
    { value: 'DOCUMENTO', label: 'Identificación' },
    { value: 'ORDEN', label: 'Orden de pedido' },
    { value: 'FACTURA', label: 'Número de factura' }
  ];
  tiposDocumentos: TipoDocumentoOption[] = [];

  formaForm: FormGroup;
  listadoFacturas: FacturaItem[] = [];
  nombreSaludo = '';
  tituloConsulta = '';
  pdfSrc: string | null = null;
  loading = false;
  errorMessage = '';
  comas = false;

  private readonly destroy$ = new Subject<void>();

  constructor(
    private fb: FormBuilder,
    private gestion: GestionService,
    private tiposDocumentoService: TiposDocumentoService,
    private uiHelper: UiHelperService
  ) {
    this.formaForm = this.fb.group(
      {
        filtro: ['', [Validators.required]],
        tipo: [{ value: 'CC', disabled: true }],
        numero: [{ value: '', disabled: true }],
        codigo: [{ value: '', disabled: true }],
        ordenPedidoId: [{ value: '', disabled: true }],
        factura: [{ value: '', disabled: true }],
        anio: [{ value: '', disabled: true }]
      },
      { validators: [this.validadorDocumentoFactura()] }
    );
  }

  ngOnInit(): void {
    this.tiposDocumentoService
      .opcionesSelect$('todos')
      .pipe(takeUntil(this.destroy$))
      .subscribe((opts) => (this.tiposDocumentos = opts));

    this.formaForm
      .get('filtro')!
      .valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((filtro) => this.aplicarValidacionesFiltro(filtro as FacturaFiltro | ''));

    this.formaForm
      .get('tipo')!
      .valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe(() => this.onTipoDocumentoChange());

    this.formaForm
      .get('numero')!
      .valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((val) => this.onNumeroChange(String(val ?? '')));

    this.formaForm
      .get('factura')!
      .valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((val) => {
        const limpio = sanitizarNumeroFactura(val);
        if (String(val ?? '') !== limpio) {
          this.formaForm.get('factura')!.setValue(limpio, { emitEvent: false });
        }
        this.actualizarValidacionAnio();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.uiHelper.hideSpinner();
  }

  get tieneResultados(): boolean {
    return this.listadoFacturas.length > 0;
  }

  get viendoPdf(): boolean {
    return !!this.pdfSrc;
  }

  get filtroActual(): FacturaFiltro | '' {
    return (this.formaForm.get('filtro')?.value as FacturaFiltro | '') || '';
  }

  get esNit(): boolean {
    return esAbreviaturaDocumentoNit(this.formaForm.get('tipo')?.value);
  }

  get mensajeNumero(): string {
    return mensajeNumeroDocumentoInvalido(this.formaForm.get('tipo')?.value);
  }

  get anioFacturaMax(): number {
    return new Date().getFullYear();
  }

  get muestraAnio(): boolean {
    return this.filtroActual === 'FACTURA' && requiereAnioFactura(this.formaForm.get('factura')?.value);
  }

  camposInvalidos(campo: string): boolean {
    const ctrl = this.formaForm.get(campo);
    return !!ctrl && ctrl.enabled && ctrl.invalid && (ctrl.touched || ctrl.dirty);
  }

  onNumeroKeyup(event: KeyboardEvent): void {
    this.comas = event.key === '.' || event.key === ',' || event.key === 'Decimal';
  }

  onFacturaKeydown(ev: KeyboardEvent): void {
    if (this.esTeclaControl(ev)) {
      return;
    }
    if (ev.key.length === 1 && /[a-zA-Z0-9]/.test(ev.key)) {
      return;
    }
    ev.preventDefault();
  }

  onFacturaPaste(ev: ClipboardEvent): void {
    ev.preventDefault();
    const limpio = sanitizarNumeroFactura(ev.clipboardData?.getData('text/plain'));
    this.formaForm.get('factura')!.setValue(limpio);
  }

  onAnioKeydown(ev: KeyboardEvent): void {
    if (this.esTeclaControl(ev)) {
      return;
    }
    if (ev.key.length === 1 && /[0-9]/.test(ev.key)) {
      return;
    }
    ev.preventDefault();
  }

  consultar(): void {
    if (this.formaForm.invalid) {
      this.formaForm.markAllAsTouched();
      return;
    }
    this.errorMessage = '';
    this.pdfSrc = null;
    this.setLoading(true);
    this.uiHelper.showSpinner(
      'Estamos cargando tus facturas…',
      'gestion',
      'Descargar factura'
    );
    this.consultarConReintento(CONSULTA_REINTENTOS);
  }

  verFactura(item: FacturaItem): void {
    const b64 = String(item.documento ?? '').trim();
    if (!b64) {
      this.errorMessage = 'La factura no tiene documento disponible.';
      return;
    }
    this.pdfSrc = `data:application/pdf;base64,${b64}`;
  }

  descargarFactura(item: FacturaItem): void {
    const b64 = String(item.documento ?? '').trim();
    if (!b64) {
      return;
    }
    const nombre = String(item.numeroFactura || item.ordenPedidoId || 'factura').replace(
      /[^\w.-]+/g,
      '_'
    );
    const a = document.createElement('a');
    a.href = `data:application/pdf;base64,${b64}`;
    a.download = `${nombre}.pdf`;
    a.click();
  }

  cerrarVistaPdf(): void {
    this.pdfSrc = null;
  }

  nuevaConsulta(): void {
    this.listadoFacturas = [];
    this.nombreSaludo = '';
    this.tituloConsulta = '';
    this.pdfSrc = null;
    this.errorMessage = '';
    this.comas = false;
    this.formaForm.reset({
      filtro: '',
      tipo: 'CC',
      numero: '',
      codigo: '',
      ordenPedidoId: '',
      factura: '',
      anio: ''
    });
    this.aplicarValidacionesFiltro('');
    this.setLoading(false);
  }

  private consultarConReintento(reintentosTrasError: number): void {
    const request = this.armarRequest();
    this.gestion
      .consultarFacturas(request)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (rows) => {
          this.setLoading(false);
          this.uiHelper.hideSpinner();
          if (!rows.length) {
            this.listadoFacturas = [];
            this.nombreSaludo = '';
            this.errorMessage = 'No se encontraron facturas disponibles.';
            return;
          }
          const bloqueado = rows.find((row) => Number(row?.ordenPedidoId) === 0);
          if (bloqueado) {
            this.listadoFacturas = [];
            this.nombreSaludo = '';
            this.errorMessage =
              String(bloqueado.descCodigo ?? '').trim() ||
              'No fue posible consultar la factura por este canal.';
            return;
          }
          this.listadoFacturas = rows;
          this.nombreSaludo = armarNombreSaludo(nombreDesdeFactura(rows[0]));
          this.tituloConsulta = this.armarTituloConsulta();
        },
        error: (error) => {
          if (reintentosTrasError > 0) {
            window.setTimeout(
              () => this.consultarConReintento(reintentosTrasError - 1),
              CONSULTA_REINTENTO_MS
            );
            return;
          }
          this.setLoading(false);
          this.uiHelper.hideSpinner();
          this.errorMessage = this.extraerMensajeError(error);
        }
      });
  }

  private armarRequest() {
    const raw = this.formaForm.getRawValue();
    const filtro = raw.filtro as FacturaFiltro;
    const data: {
      numeroFactura: string;
      ordenPedidoId: number | string;
      numeroIdentificacion: string | null;
      anio?: string;
    } = {
      numeroFactura: filtro === 'FACTURA' ? sanitizarNumeroFactura(raw.factura) : '',
      ordenPedidoId: filtro === 'ORDEN' ? String(raw.ordenPedidoId ?? '').trim() || 0 : 0,
      numeroIdentificacion: null
    };

    if (filtro === 'DOCUMENTO') {
      const numero = String(raw.numero ?? '').trim().toUpperCase();
      const codigo = String(raw.codigo ?? '').trim();
      data.numeroIdentificacion = this.esNit ? `${numero}${codigo}` : numero || null;
    }

    if (filtro === 'FACTURA' && requiereAnioFactura(raw.factura)) {
      data.anio = String(raw.anio ?? '').trim();
    }
    return data;
  }

  private armarTituloConsulta(): string {
    const raw = this.formaForm.getRawValue();
    const filtro = raw.filtro as FacturaFiltro;
    if (filtro === 'DOCUMENTO') {
      const tipoUi = abreviaturaCanon(raw.tipo);
      const label =
        this.tiposDocumentos.find((t) => t.value === tipoUi)?.label || tipoUi;
      const numero = String(raw.numero ?? '').trim();
      const dv = this.esNit && raw.codigo ? `-${raw.codigo}` : '';
      return `${label}: ${numero}${dv}`;
    }
    if (filtro === 'ORDEN') {
      return `Orden de pedido: ${raw.ordenPedidoId}`;
    }
    return `Factura: ${sanitizarNumeroFactura(raw.factura)}`;
  }

  private aplicarValidacionesFiltro(filtro: FacturaFiltro | ''): void {
    const campos = ['tipo', 'numero', 'codigo', 'ordenPedidoId', 'factura', 'anio'] as const;
    for (const name of campos) {
      const ctrl = this.formaForm.get(name)!;
      ctrl.clearValidators();
      ctrl.setValue(name === 'tipo' ? 'CC' : '', { emitEvent: false });
      ctrl.disable({ emitEvent: false });
    }

    if (filtro === 'DOCUMENTO') {
      this.enable('tipo', [Validators.required]);
      this.enable('numero', [Validators.required]);
      this.onTipoDocumentoChange();
    } else if (filtro === 'ORDEN') {
      this.enable('ordenPedidoId', [
        Validators.required,
        Validators.minLength(7),
        Validators.maxLength(10),
        Validators.pattern(/^[0-9]+$/)
      ]);
    } else if (filtro === 'FACTURA') {
      this.enable('factura', [
        Validators.required,
        Validators.minLength(6),
        Validators.pattern(/^[a-zA-Z0-9]{6,30}$/)
      ]);
      this.actualizarValidacionAnio();
    }
    this.formaForm.updateValueAndValidity({ emitEvent: false });
  }

  private onTipoDocumentoChange(): void {
    if (this.filtroActual !== 'DOCUMENTO') {
      return;
    }
    const codigo = this.formaForm.get('codigo')!;
    if (this.esNit) {
      codigo.enable({ emitEvent: false });
      codigo.setValidators([
        Validators.required,
        Validators.min(0),
        Validators.max(9),
        Validators.pattern(/^[0-9]$/)
      ]);
      this.onNumeroChange(String(this.formaForm.get('numero')?.value ?? ''));
    } else {
      codigo.clearValidators();
      codigo.setValue('', { emitEvent: false });
      codigo.disable({ emitEvent: false });
    }
    codigo.updateValueAndValidity({ emitEvent: false });
  }

  private onNumeroChange(raw: string): void {
    if (this.filtroActual !== 'DOCUMENTO') {
      return;
    }
    const tipo = this.formaForm.get('tipo')?.value;
    const limpio = sanitizarNumeroDocumento(tipo, raw);
    const numeroCtrl = this.formaForm.get('numero')!;
    if (limpio !== raw) {
      numeroCtrl.setValue(limpio, { emitEvent: false });
    }
    if (this.esNit && limpio.length >= 6) {
      const dv = calcularDigitoVerificacionNIT(limpio);
      this.formaForm.get('codigo')!.setValue(dv, { emitEvent: false });
    }
    this.formaForm.updateValueAndValidity({ emitEvent: false });
  }

  private validadorDocumentoFactura(): ValidatorFn {
    return (group: AbstractControl): ValidationErrors | null => {
      if (group.get('filtro')?.value !== 'DOCUMENTO') {
        return null;
      }
      const tipo = group.get('tipo')?.value;
      const numero = group.get('numero')?.value;
      const numCtrl = group.get('numero');
      if (!tipo || !String(numero ?? '').trim()) {
        return null;
      }
      if (numeroDocumentoInvalido(tipo, numero)) {
        numCtrl?.setErrors({ ...(numCtrl.errors || {}), sincodigo: true });
        return { sincodigo: true };
      }
      if (numCtrl?.hasError('sincodigo')) {
        const { sincodigo: _drop, ...rest } = numCtrl.errors || {};
        numCtrl.setErrors(Object.keys(rest).length ? rest : null);
      }
      return null;
    };
  }

  private actualizarValidacionAnio(): void {
    const anio = this.formaForm.get('anio')!;
    if (this.muestraAnio) {
      anio.enable({ emitEvent: false });
      anio.setValidators([Validators.required, this.validadorAnio()]);
    } else {
      anio.clearValidators();
      anio.setValue('', { emitEvent: false });
      anio.disable({ emitEvent: false });
    }
    anio.updateValueAndValidity({ emitEvent: false });
  }

  private validadorAnio(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const val = String(control.value ?? '').trim();
      if (!val) {
        return null;
      }
      if (!/^[0-9]{4}$/.test(val)) {
        return { anioFormato: true };
      }
      const anio = parseInt(val, 10);
      if (anio < FACTURA_ANIO_MIN || anio > new Date().getFullYear()) {
        return { anioRango: true };
      }
      return null;
    };
  }

  private enable(name: string, validators: ValidatorFn[]): void {
    const ctrl = this.formaForm.get(name)!;
    ctrl.enable({ emitEvent: false });
    ctrl.setValidators(validators);
    ctrl.updateValueAndValidity({ emitEvent: false });
  }

  private esTeclaControl(ev: KeyboardEvent): boolean {
    return (
      ev.key === 'Backspace' ||
      ev.key === 'Delete' ||
      ev.key === 'Tab' ||
      ev.key === 'Escape' ||
      ev.key === 'Enter' ||
      ev.key.startsWith('Arrow') ||
      ev.key === 'Home' ||
      ev.key === 'End' ||
      ev.ctrlKey ||
      ev.metaKey ||
      ev.altKey
    );
  }

  private setLoading(loading: boolean): void {
    this.loading = loading;
    if (loading) {
      this.formaForm.disable({ emitEvent: false });
      return;
    }
    // Rehabilita solo los controles del filtro activo (sin limpiar valores).
    const filtro = this.filtroActual;
    this.formaForm.get('filtro')?.enable({ emitEvent: false });
    if (filtro === 'DOCUMENTO') {
      this.formaForm.get('tipo')?.enable({ emitEvent: false });
      this.formaForm.get('numero')?.enable({ emitEvent: false });
      if (this.esNit) {
        this.formaForm.get('codigo')?.enable({ emitEvent: false });
      }
    } else if (filtro === 'ORDEN') {
      this.formaForm.get('ordenPedidoId')?.enable({ emitEvent: false });
    } else if (filtro === 'FACTURA') {
      this.formaForm.get('factura')?.enable({ emitEvent: false });
      if (this.muestraAnio) {
        this.formaForm.get('anio')?.enable({ emitEvent: false });
      }
    }
  }

  private extraerMensajeError(error: {
    error?: { mensaje?: string; descripcionErrores?: string[]; message?: string };
    message?: string;
  }): string {
    return (
      error?.error?.mensaje ||
      (Array.isArray(error?.error?.descripcionErrores)
        ? error.error.descripcionErrores.join('\n')
        : null) ||
      error?.error?.message ||
      error?.message ||
      'No fue posible consultar la factura.'
    );
  }
}
